import { ChevronLeft, Disc3, Pause, Play } from 'lucide-react';

import type { TrackDto } from '../api';
import type { SpectrumParams } from '../audio/spectrum';
import type { WaveformData } from '../audio/waveform-data';
import type { WaveformParams } from '../audio/waveform-render';
import type { Player } from '../hooks/usePlayer';
import { formatBytes, formatDuration } from './format';
import { TweakPanel } from './player/TweakPanel';
import { Waveform } from './player/Waveform';

type TrackViewProps = {
  track: TrackDto;
  player: Player;
  waveform: WaveformData | null;
  waveformPending: boolean;
  waveformParams: WaveformParams;
  spectrumParams: SpectrumParams;
  onWaveformParams(params: WaveformParams): void;
  onSpectrumParams(params: SpectrumParams): void;
  onBack(): void;
};

/**
 * The full view for one set: a large waveform that doubles as the seek surface, then
 * the metadata and the render-parameter inspector.
 *
 * It carries no spectrum analyser of its own — the dock's runs on every page, and two
 * of them on one screen showed the same instant twice. The waveform is what this page
 * exists for, so it gets the room.
 */
export function TrackView({
  track,
  player,
  waveform,
  waveformPending,
  waveformParams,
  spectrumParams,
  onWaveformParams,
  onSpectrumParams,
  onBack,
}: TrackViewProps) {
  const isCurrent = player.track?.id === track.id;
  const duration = (isCurrent ? player.duration : 0) || track.durationSeconds || 0;
  const progress = isCurrent && duration > 0 ? Math.min(player.time / duration, 1) : 0;

  return (
    <div className="track-view">
      <button type="button" className="back-link" onClick={onBack}>
        <ChevronLeft size={15} /> Library
      </button>

      <div className="waveform-stage">
        <Waveform
          data={waveform}
          params={waveformParams}
          progress={progress}
          duration={duration}
          onSeek={(fraction) => {
            if (isCurrent) player.seekToFraction(fraction);
            else void player.play(track).then(() => player.seekToFraction(fraction));
          }}
          label={`Seek within ${track.title}`}
        />
        <div className="waveform-times numeric">
          <span>{formatDuration(isCurrent ? player.time : 0)}</span>
          <span>{formatDuration(duration)}</span>
        </div>
        {waveformPending && (
          <p className="waveform-pending">
            Analysing waveform — playback works now, the shape fills in shortly.
          </p>
        )}
      </div>

      <TweakPanel
        waveform={waveformParams}
        spectrum={spectrumParams}
        onWaveformChange={onWaveformParams}
        onSpectrumChange={onSpectrumParams}
      />

      <div className="track-hero">
        <div className="track-hero-cover">
          {track.coverUrl ? (
            <img src={track.coverUrl} alt="" />
          ) : (
            <div className="track-cover-empty">
              <Disc3 />
            </div>
          )}
        </div>

        <div className="track-hero-info">
          <h1>{track.title}</h1>
          <p className="track-artist">{track.artist || 'Unknown artist'}</p>

          <div className="track-facts numeric">
            <button
              type="button"
              className="button primary"
              disabled={track.status !== 'ready'}
              onClick={() => (isCurrent ? player.toggle() : void player.play(track))}
            >
              {isCurrent && player.playing ? (
                <>
                  <Pause size={15} fill="currentColor" /> Pause
                </>
              ) : (
                <>
                  <Play size={15} fill="currentColor" /> Play
                </>
              )}
            </button>
            <span className="track-fact">
              <span className="label">Length</span>
              <strong>{formatDuration(track.durationSeconds)}</strong>
            </span>
            <span className="track-fact">
              <span className="label">Size</span>
              <strong>{formatBytes(track.sizeBytes)}</strong>
            </span>
            {track.genre && (
              <span className="track-fact">
                <span className="label">Genre</span>
                <strong>{track.genre}</strong>
              </span>
            )}
            {track.recordedYear && (
              <span className="track-fact">
                <span className="label">Year</span>
                <strong>{track.recordedYear}</strong>
              </span>
            )}
          </div>

          {track.description && <p className="track-description">{track.description}</p>}
        </div>
      </div>
    </div>
  );
}
